import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Code, FileText, Fingerprint, Network, Sparkles, User as UserIcon, UserCog, MessageSquareText } from 'lucide-react';
import { Modal, Button, cn } from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import { formatIst } from '../../lib/time';
import { MODULE_BADGE, actionBadge, diffChangedFields, redactSensitive, humanLabel, logHref } from './activityConstants';
import { LogValue } from './logValueView';
import { groupPayloadFields, FieldGroupCard, fieldLabel, fieldSortRank, chipFor, isWide, isRef, RefValue, redactedNote, isFromRecord, FromRecordValue } from './logPayloadFields';
import { describeClient, describeIp, type DeviceInfo } from './clientOrigin';

// Device facts the "Came in from" cell already shows, so the Details table leaves them out — plus
// `reused`, an internal flag older push-device rows carry that only repeats their own sentence
// ("Push device RE-registered" is exactly reused = true), which read as a meaningless "Reused: Yes".
const ORIGIN_KEYS = new Set(['platform', 'devicemodel', 'appversion', 'deviceid', 'ipaddress', 'useragent', 'reused']);

// Housekeeping columns that are just noise in an audit view — never surface them as a "change" or a
// snapshot field (matched case/underscore-insensitively so createdAt & created_at both drop out).
const NOISE_KEYS = new Set(['id', 'uuid', 'createdat', 'updatedat', 'deletedat', 'createdby', 'updatedby', 'version']);
function stripNoise(v: any): any {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    if (!NOISE_KEYS.has(k.toLowerCase().replace(/_/g, ''))) out[k] = val;
  }
  return out;
}

export interface ActorRef { id: string; name: string; role: string; phone?: string | null }
export interface ActivityRow {
  id: string;
  module: string;
  action: string;
  referenceId: string | null;
  referenceLabel?: string | null;
  /** Where that record is (a dealer's village) — its own fact, never glued onto the label. */
  referencePlace?: string | null;
  linkModule?: string | null;
  linkId?: string | null;
  oldValue: any;
  newValue: any;
  message: string | null;
  performedBy: string | null;
  actingOnBehalfOf: string | null;
  source: string | null;
  ipAddress: string | null;
  userAgent?: string | null;
  /** From the login session the row came through; null before 2026-09-28 or with no session. */
  device?: DeviceInfo | null;
  createdAt: string;
  actor: ActorRef | null;
  onBehalfOf: ActorRef | null;
}

// Parse `<sentence> (field_a, field_b)` → { prefix, fields[] } so the trailing
// changed-field list renders as readable chips instead of snake_case noise.
function parseTrailingFieldList(message?: string | null): { prefix: string; fields: string[] } {
  if (!message) return { prefix: '', fields: [] };
  const m = message.match(/^(.*?)\s*\(([\w-]+(?:\s*,\s*[\w-]+)+)\)\s*$/);
  if (!m) return { prefix: message, fields: [] };
  const fields = m[2].split(',').map((s) => s.trim()).filter(Boolean);
  if (fields.length < 2) return { prefix: message, fields: [] };
  return { prefix: m[1].trim(), fields };
}

function softenEnum(s: unknown): string {
  if (typeof s !== 'string') return String(s);
  if (!/^[A-Z][A-Z0-9_]+$/.test(s)) return s;
  return s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (/^-?\d+\.\d+$/.test(s)) { const n = Number(s); if (Number.isFinite(n)) return n.toString(); }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) return formatIst(s);
  return s;
}

/** "10m ago" — the age of the entry, which is what a reader checks first on an audit trail. */
function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

/**
 * What KIND of thing the entry records, so the dialog can be coloured by what happened rather than
 * arriving in the same grey for a deletion and a login. The action vocabulary is open-ended (every
 * module coins its own verbs), so these are patterns rather than a key list — an unrecognised verb
 * lands on `edit`, which is the honest default for "something was written".
 */
type Kind = 'create' | 'destroy' | 'flow' | 'shift' | 'io' | 'edit';
const KIND_RULES: Array<[RegExp, Kind]> = [
  [/DELET|REMOV|CANCEL|REJECT|DEACTIVAT|UNASSIGN|FAIL|PASSWORD|VOID/, 'destroy'],
  [/CREAT|APPROV|ACTIVAT|DELIVER|PAYMENT|CONVERT|TOPPED|RESTOR|LOGIN|CHECKED|VERIFI/, 'create'],
  [/IMPORT|EXPORT|UPLOAD|GENERAT/, 'io'],
  [/SUBMIT|ASSIGN|CLAIM|DISPATCH|SENT|CONTACT/, 'flow'],
  [/STATUS|ADJUST|TRANSFER|REOPEN|REGULARIZ|RETURN|REFUND|DRAFT|ARCHIV/, 'shift'],
];
const kindOf = (action: string): Kind => KIND_RULES.find(([re]) => re.test(action))?.[1] ?? 'edit';

/** The band's tint per kind. Light gradient into the surface so the dialog body reads as one sheet. */
const BAND: Record<Kind, string> = {
  create: 'border-emerald-200 from-emerald-50 dark:border-emerald-900/40 dark:from-emerald-900/20',
  destroy: 'border-rose-200 from-rose-50 dark:border-rose-900/40 dark:from-rose-900/20',
  shift: 'border-amber-200 from-amber-50 dark:border-amber-900/40 dark:from-amber-900/20',
  flow: 'border-cyan-200 from-cyan-50 dark:border-cyan-900/40 dark:from-cyan-900/20',
  io: 'border-purple-200 from-purple-50 dark:border-purple-900/40 dark:from-purple-900/20',
  edit: 'border-blue-200 from-blue-50 dark:border-blue-900/40 dark:from-blue-900/20',
};
/** The matching hue for the band's small caption icons. */
const BAND_ICON: Record<Kind, string> = {
  create: 'text-emerald-600 dark:text-emerald-400',
  destroy: 'text-rose-600 dark:text-rose-400',
  shift: 'text-amber-600 dark:text-amber-400',
  flow: 'text-cyan-600 dark:text-cyan-400',
  io: 'text-purple-600 dark:text-purple-400',
  edit: 'text-blue-600 dark:text-blue-400',
};

function ObjectCard({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <span className="italic text-muted">—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-2xs leading-tight">
          <span className="min-w-[80px] text-muted">{humanLabel(k)}</span>
          <span className="break-words text-content">
            {v && typeof v === 'object' ? <ObjectCard data={v as Record<string, unknown>} /> : softenEnum(formatScalar(v))}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderCell(value: unknown): ReactNode {
  if (value === undefined || value === null || value === '') return <span className="italic text-muted">—</span>;
  // A diff row has no field name to judge by, so a scrubbed value says only that it was not stored.
  const scrubbed = redactedNote(null, value);
  if (scrubbed) return scrubbed;
  // A resolved reference ({ __ref, id, name, role }) is a person or a record, not a nested object to
  // unfold — it renders as the link + role badge the rest of the app uses.
  if (isRef(value)) return <RefValue value={value} />;
  // A value this entry lost, carried over from the record (see logPayloadFields).
  if (isFromRecord(value)) return <FromRecordValue value={value} />;
  if (typeof value === 'boolean') return value ? <span className="font-medium text-emerald-600">Yes</span> : <span className="font-medium text-rose-600">No</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="italic text-muted">empty</span>;
    if (value.every((v) => v && typeof v === 'object')) {
      return <div className="space-y-1.5">{value.map((it, i) => <div key={i} className="rounded-control border border-line bg-surface px-2 py-1.5"><ObjectCard data={it as Record<string, unknown>} /></div>)}</div>;
    }
    return <div className="flex flex-wrap gap-1">{value.map((v, i) => <span key={i} className="inline-block rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-content">{softenEnum(formatScalar(v))}</span>)}</div>;
  }
  if (typeof value === 'object') return <ObjectCard data={value as Record<string, unknown>} />;
  return <span className="break-words text-content">{formatScalar(value)}</span>;
}

/** One labelled cell of the identity band. */
function BandCell({ label, icon, children, className }: { label: string; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex items-center gap-1.5 type-overline text-subtle">{icon}{label}</div>
      <div className="mt-1 min-w-0 type-body text-content">{children}</div>
    </div>
  );
}

/** A titled block of the body, so every part of the entry is announced the same way. */
function Section({ title, icon, count, children, className, titleClassName }: {
  title: string; icon?: ReactNode; count?: number; children: ReactNode; className?: string; titleClassName?: string;
}) {
  return (
    <section className={cn('mt-4 rounded-card border p-3.5', className ?? 'border-line bg-surface-2/30')}>
      <h3 className={cn('mb-2.5 inline-flex items-center gap-1.5 type-overline', titleClassName ?? 'text-muted')}>
        {icon}{title}{count != null && ` (${count})`}
      </h3>
      {children}
    </section>
  );
}

/**
 * One activity-log entry, in full.
 *
 * This was a right-hand drawer of stacked label/value pairs: it re-stated the module and action four
 * times over, buried the two things a reader is actually after (WHO did it and WHAT changed) below
 * three identical grey cards, and never showed the origin or IP the row already carried. A centered
 * dialog gives the before→after table room to be read across, the band up top answers who/what/where
 * in one line each, and the colour comes from the ACTION — a deletion should not arrive in the same
 * grey as a login.
 */
export function ActivityLogDetailModal({ log, onClose }: { log: ActivityRow; onClose: () => void }) {
  const navigate = useNavigate();
  const [showRaw, setShowRaw] = useState(false);
  const oldRaw = redactSensitive(log.oldValue);
  const newRaw = redactSensitive(log.newValue);
  // The diff + snapshot show only MEANINGFUL fields (housekeeping columns stripped); the "full snapshot"
  // below keeps everything for power users.
  const oldData = stripNoise(oldRaw);
  const newData = stripNoise(newRaw);
  const changed = diffChangedFields(oldData, newData);
  const oldObj = oldData && typeof oldData === 'object' && !Array.isArray(oldData) ? (oldData as Record<string, unknown>) : null;
  const newObj = newData && typeof newData === 'object' && !Array.isArray(newData) ? (newData as Record<string, unknown>) : null;
  const nonEmpty = (o: Record<string, unknown> | null) => !!o && Object.values(o).some((v) => v !== null && v !== undefined && v !== '');
  const hasOld = nonEmpty(oldObj);
  const hasNew = nonEmpty(newObj);
  // A true before→after only when BOTH sides exist; otherwise render a labeled one-sided snapshot so a
  // create / delete / import job doesn't show confusing "— → x" or "x → —" rows.
  const diffMode = hasOld && hasNew && changed.length > 0;
  const isDelete = /DELETE|REMOV/i.test(log.action);
  const snapshotObj = diffMode ? null : hasNew ? newObj : hasOld ? oldObj : null;
  // A detail that only repeats the WHO box (a login's own mobile) is dropped: printed three times —
  // under Who, in Details, and again in the snapshot — it read as three different facts.
  const repeatsWho = (v: unknown) => !!log.actor?.phone && String(v) === String(log.actor.phone);
  // …and a detail that "Came in from" already shows — properly written ("Android", not the payload's
  // raw "android") — is not printed a second time underneath it.
  const shownAsOrigin = (k: string) => ORIGIN_KEYS.has(k.toLowerCase().replace(/_/g, ''));
  const snapshotEntries = Object.entries(snapshotObj || {}).filter(([k, v]) => v !== null && v !== undefined && v !== '' && !repeatsWho(v) && !shownAsOrigin(k));
  // Grouped by meaning and ordered for reading — see logPayloadFields.tsx.
  const snapshotGroups = groupPayloadFields(snapshotEntries);
  // "Show full snapshot" only when the raw payload holds something the sections above do not already
  // show — otherwise it opened onto the very same line again ("before and after are same").
  const rawKeys = new Set([...Object.entries(oldRaw && typeof oldRaw === 'object' ? oldRaw : {}), ...Object.entries(newRaw && typeof newRaw === 'object' ? newRaw : {})]
    .filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k]) => k));
  const shownKeys = new Set(diffMode ? changed.map((c) => c.field) : Object.keys(snapshotObj || {}));
  const rawHasMore = [...rawKeys].some((k) => !shownKeys.has(k)) || (diffMode && hasOld && hasNew);
  const snapshotTitle = isDelete ? 'Removed record'
    : /IMPORT/i.test(log.action) ? 'Import summary'
    : /CREATE|REGISTER|ADD/i.test(log.action) ? 'Created'
    : 'Details';
  // logHref, not entityHref: a shipment row's own reference is the shipment, but the record a reader
  // wants is the ORDER it belongs to — the server says so via linkModule/linkId.
  const href = logHref(log);
  const actor = log.actor;
  const { prefix, fields } = parseTrailingFieldList(log.message);
  const kind = kindOf(log.action);
  const client = describeClient(log);
  const ip = describeIp(log.ipAddress);
  // Record earns its cell only with a real document number (GB/2026/06/00004). "No document number",
  // or a sign-in's own account, only repeated the module chip in the header or the Who cell beside it.
  const showRecord = !!log.referenceLabel;

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={880}
      title={<span className="block truncate">{prefix || `${humanLabel(log.module)} ${softenEnum(log.action).toLowerCase()}`}</span>}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn('inline-block rounded px-2 py-0.5 type-caption', MODULE_BADGE[log.module] ?? 'bg-surface-2 text-content')}>{log.module}</span>
          <span className={cn('inline-block rounded px-2 py-0.5 type-caption', actionBadge(log.action))}>{log.action.replace(/_/g, ' ')}</span>
          {/* The entry's own database number ("#27307") is not shown: it names nothing a reader
              looks up (owner, 2026-09-28). It stays on hover, for pointing a developer at one row. */}
          <span className="text-muted" title={`Log entry ${log.id}`}>{formatIst(log.createdAt)}</span>
          <span className="text-subtle">· {relTime(log.createdAt)}</span>
        </span>
      }
      footer={
        <>
          {href && (
            <Button variant="primary" className="mr-auto" icon={<FileText size={16} />}
              onClick={() => { onClose(); navigate(href); }}>
              Open {humanLabel(log.linkModule ?? log.module).toLowerCase()}
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {/* ── Identity band: who acted, what they touched, and where the action came in from. Three
             columns rather than three stacked cards — these are one sentence, not three topics. ── */}
      <div className={cn('grid gap-3 rounded-card border bg-gradient-to-br to-surface p-3.5 sm:gap-0 sm:divide-x sm:divide-line', showRecord ? 'sm:grid-cols-4' : 'sm:grid-cols-3', BAND[kind])}>
        <BandCell label="Who" icon={<UserIcon className={cn('size-3', BAND_ICON[kind])} />} className="sm:pr-4">
          {actor ? (
            <>
              {/* The name wraps rather than truncating, and the role flows right after it — beside
                  the name, never dropped onto a line of its own (owner, 2026-09-28). */}
              <div className="wrap-break-word type-body-strong">
                {actor.name || `#${actor.id}`}
                <span className="ml-1.5 inline-block align-middle"><RoleBadge role={actor.role} /></span>
              </div>
              {actor.phone && (
                <a href={`tel:${actor.phone}`} className="mt-0.5 block truncate font-mono type-caption text-brand-700 hover:underline dark:text-brand-400">{actor.phone}</a>
              )}
            </>
          ) : (
            <span className="italic text-muted">No actor recorded — written by the system</span>
          )}
          {log.onBehalfOf && (
            // ON_BEHALF is the one path where the name on the row is not the person who pressed the
            // button, so it is called out here rather than left to the payload.
            <div className="mt-1.5 inline-flex flex-wrap items-center gap-1 rounded-control bg-plum-500/10 px-2 py-1 type-caption text-plum-600 dark:text-plum-400">
              <UserCog className="size-3" /> on behalf of {log.onBehalfOf.name}
            </div>
          )}
        </BandCell>

        {showRecord && (
          <BandCell label="Record" icon={<FileText className={cn('size-3', BAND_ICON[kind])} />} className="sm:px-4">
            {/* Never a bare database id ("#69"): it names nothing a reader can look up. */}
            {/* A NAME is not a document number, so it is not set in mono — that broke "Jay Malhar
                Krishi Kendra" mid-word. What it is and where it is reads as a sentence underneath:
                a name with " · Nimon" stuck on the end left the reader asking what Nimon was
                (owner, 2026-09-29). The name itself opens the record. */}
            {href ? (
              <button type="button" onClick={() => { onClose(); navigate(href); }}
                className="block max-w-full wrap-break-word text-left type-body-strong text-brand-700 hover:underline dark:text-brand-400">
                {log.referenceLabel}
              </button>
            ) : (
              <span className="block wrap-break-word type-body-strong">{log.referenceLabel}</span>
            )}
            <span className="mt-0.5 block wrap-break-word type-caption text-subtle">
              {/* `softenEnum`, not `humanLabel`: in a sentence the module is a word ("Retailer in
                  Nimon"), not the shouted enum the badge above it wears. */}
              {log.referencePlace ? `${softenEnum(log.module)} in ${log.referencePlace}` : softenEnum(log.module)}
            </span>
          </BandCell>
        )}

        <BandCell label="Came in from" icon={<Fingerprint className={cn('size-3', BAND_ICON[kind])} />} className="sm:px-4">
          {/* Read off the request itself (session → app version + phone model; user agent → browser
              and OS), no longer a defaulted "Web app" beside an Android row's own "platform: android". */}
          <span className={cn('inline-flex flex-wrap items-center gap-1.5', client.kind === 'UNKNOWN' && 'italic text-muted')}>
            {client.icon}{client.label}
            {client.version && <span className="rounded bg-surface-2 px-1.5 font-mono type-caption text-muted">{client.version}</span>}
          </span>
          {client.detail && <span className="mt-0.5 block truncate type-caption text-muted" title={client.detail}>{client.detail}</span>}
          {log.device?.deviceId && <span className="mt-0.5 block truncate font-mono type-caption text-subtle" title="Device ID">ID {log.device.deviceId}</span>}
        </BandCell>

        {/* The IP as a fact of its own, BESIDE the device — not a grey line tucked under it
            (owner, 2026-09-28: "show the IP in a proper way beside"). */}
        <BandCell label="IP address" icon={<Network className={cn('size-3', BAND_ICON[kind])} />} className="sm:pl-4">
          {ip ? (
            <>
              <span className="block truncate font-mono type-body-strong" title={ip.title}>{ip.address}</span>
              <span className="mt-0.5 block type-caption text-muted">{ip.where}</span>
            </>
          ) : (
            <>
              <span className="block italic text-muted">Not recorded</span>
              <span className="mt-0.5 block type-caption text-subtle">logged before IPs were kept</span>
            </>
          )}
        </BandCell>
      </div>

      {/* ── What the entry says, in its own words ── */}
      {/* The dialog's title IS the entry's sentence, so it is not printed again under "What happened".
          What the sentence cannot carry — the list of fields it touched — stays, as chips. */}
      {fields.length > 0 && (
        <Section title="Fields touched" icon={<MessageSquareText className="size-3.5" />}>
          <div className="flex flex-wrap items-center gap-1.5">
            {fields.map((f) => <span key={f} className="inline-block rounded bg-amber-100 px-2 py-0.5 type-caption text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{humanLabel(f)}</span>)}
          </div>
        </Section>
      )}

      {/* ── Genuine edit → a real before → after diff ── */}
      {diffMode && (
        <Section
          title="What changed" count={changed.length}
          icon={<Sparkles className="size-3.5 text-amber-500" />}
          className="border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-900/10"
          titleClassName="text-amber-700 dark:text-amber-300"
        >
          <div className="overflow-x-auto rounded-control border border-amber-100 bg-surface dark:border-amber-900/30">
            <table className="w-full min-w-[520px] text-xs">
              <thead className="bg-amber-50/60 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <tr>
                  <th className="px-3 py-2 text-left type-overline">Field</th>
                  <th className="px-3 py-2 text-left type-overline">Before</th>
                  <th className="w-6 px-1 py-2" />
                  <th className="px-3 py-2 text-left type-overline">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {/* Same reading order as a created record: related fields sit together instead of
                    arriving in whatever order the payload was written in. */}
                {[...changed].sort((a, b) => fieldSortRank(a.field) - fieldSortRank(b.field)).map((c) => (
                  <tr key={c.field} className="align-top">
                    <td className="whitespace-nowrap px-3 py-2 font-semibold text-content">{fieldLabel(c.field)}</td>
                    <td className="max-w-[220px] px-3 py-2 text-muted line-through decoration-rose-300 decoration-1">{renderCell(c.old)}</td>
                    <td className="px-1 py-2 text-amber-400"><ArrowRight className="size-3.5" /></td>
                    <td className="max-w-[220px] px-2 py-2">
                      <div className="rounded-md bg-amber-50/50 px-2 py-1.5 ring-2 ring-amber-300 dark:bg-amber-900/20 dark:ring-amber-700">{renderCell(c.new)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Create / delete / import (one-sided) → the record as a reader would read it ──

             Not a flat key/value table in payload order: that put "Company Name" seventh, split a
             map pin across two rows and printed a beat as `126`. The fields are grouped by meaning
             (identity → contact → where → documents → …), in the same sections and tints the
             retailer and farmer detail views use, so a dealer created here looks like the same
             dealer opened there. */}
      {!diffMode && snapshotGroups.length > 0 && (
        <Section
          title={snapshotTitle} count={snapshotEntries.length}
          icon={<FileText className="size-3.5" />}
          className={isDelete ? 'border-rose-200 bg-rose-50/30 dark:border-rose-900/40 dark:bg-rose-900/10' : undefined}
          titleClassName={isDelete ? 'text-rose-700 dark:text-rose-300' : undefined}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {snapshotGroups.map((g) => (
              // A group of one or two short fields sits beside its neighbour; a long one (items, an
              // address block) takes the full width rather than squeezing into half of it.
              <FieldGroupCard key={g.key} group={g} className={cn(g.entries.length > 2 && 'sm:col-span-2', isDelete && 'opacity-90')}>
                <dl className={cn('grid gap-x-6 gap-y-3', g.entries.length > 2 ? 'sm:grid-cols-2' : 'grid-cols-1')}>
                  {g.entries.map(([k, v]) => (
                    <div key={k} className={cn('min-w-0', isWide(v) && 'sm:col-span-2')}>
                      <dt className="type-caption text-subtle">{fieldLabel(k)}</dt>
                      <dd className={cn('mt-0.5 min-w-0 wrap-break-word type-body-strong text-content', isDelete && 'text-muted')}>
                        {isRef(v) ? <RefValue value={v} />
                          : isFromRecord(v) ? <FromRecordValue field={k} value={v} />
                          : chipFor(k, v) ?? renderCell(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </FieldGroupCard>
            ))}
          </div>
        </Section>
      )}

      {/* ── Technical view — the COMPLETE payload (housekeeping fields included) ── */}
      {rawHasMore && (
        <div className="mt-4">
          <button type="button" onClick={() => setShowRaw((v) => !v)} className="inline-flex items-center gap-1.5 type-caption text-muted hover:text-content">
            <Code className="size-3" /> {showRaw ? 'Hide full snapshot' : 'Show full snapshot'}
          </button>
          {showRaw && (
            <div className="mt-2 space-y-3">
              {oldRaw != null && (
                <div className="rounded-control border border-line">
                  <div className="px-3 py-2 type-overline text-muted">Before</div>
                  <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={oldRaw} /></div>
                </div>
              )}
              {newRaw != null && (
                <div className="rounded-control border border-line">
                  <div className="px-3 py-2 type-overline text-muted">After</div>
                  <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={newRaw} /></div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
